<template>
  <Teleport to="body">
    <div v-if="isVisible" class="modal-overlay" :class="{ dark: isDark }" @click.stop>
      <div class="modal-content" @click.stop>
        <div class="header-container">
          <h2 class="modal-title">Historial de Pagos - {{ proveedor?.nombre_proveedor }}</h2>
          <button @click="$emit('close')" class="btn-close">&times;</button>
        </div>

        <div class="info-box">
          <div class="info-item"><strong>Proveedor:</strong> {{ proveedor?.nombre_proveedor }}</div>
          <div class="info-item"><strong>RTN:</strong> {{ proveedor?.rtn_proveedor }}</div>
          <div class="info-item">
            <strong>Estado:</strong>
            <span :class="['estado-badge', 'estado-' + (proveedor?.estado || '').toLowerCase()]">
              {{ proveedor?.estado }}
            </span>
          </div>
        </div>

        <div class="resumen-cuenta">
          <div class="resumen-item">
            <span>Monto Original Total:</span>
            <span>{{ formatCurrency(proveedor?.monto_original_total) }}</span>
          </div>
          <div class="resumen-item">
            <span>Total Pagado:</span>
            <span class="pagado">{{ formatCurrency(proveedor?.monto_pagado_total) }}</span>
          </div>
          <div class="resumen-item">
            <span>Saldo Pendiente:</span>
            <span class="pendiente">{{ formatCurrency(proveedor?.saldo_pendiente_total) }}</span>
          </div>
        </div>

        <div class="historial-section">
          <h3>Historial de Pagos</h3>
          <div v-if="cargando" class="loading">Cargando historial...</div>
          <div v-else-if="historial.length === 0" class="no-data">
            No hay pagos registrados para este proveedor
          </div>
          <div v-else class="historial-list">
            <div v-for="pago in historial" :key="pago.id_pago_proveedor" class="pago-item">
              <div class="pago-header">
                <div class="pago-fecha">
                  <i class="bi bi-calendar3"></i>
                  {{ formatDateTime(pago.fecha_pago) }}
                </div>
                <div class="pago-monto">{{ formatCurrency(pago.monto_pago) }}</div>
              </div>
              <div class="pago-details">
                <div class="pago-metodo">
                  <i class="bi bi-credit-card"></i>
                  {{ pago.metodo_pago }}
                  <span v-if="pago.numero_transferencia" class="transfer-number">({{ pago.numero_transferencia }})</span>
                </div>
                <div class="pago-factura">
                  <i class="bi bi-receipt"></i>
                  Factura: {{ pago.numero_factura }}
                </div>
                <div v-if="pago.notas" class="pago-notas">
                  <i class="bi bi-chat-text"></i> {{ pago.notas }}
                </div>
                <div class="pago-usuario">
                  <i class="bi bi-person"></i>
                  Procesado por: {{ pago.nombre_usuario }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="button-container">
          <button @click="$emit('close')" class="btn btn-cerrar">Cerrar</button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import CuentasPorPagarService from "../../services/cuentasPorPagarSolicitudes";
import { useToast } from "vue-toastification";

export default {
  name: "HistorialPagosProveedorModal",
  props: {
    isVisible: { type: Boolean, required: true },
    proveedor: { type: Object, required: true },
    id_usuario: { type: Number, required: true },
  },
  emits: ['close'],
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return { isDark: false, cargando: false, historial: [] };
  },
  methods: {
    formatCurrency(v) { return `L. ${Number(v).toFixed(2)}`; },
    formatDateTime(d) {
      return new Date(d).toLocaleString("es-HN", {
        year: "numeric", month: "2-digit", day: "2-digit",
        hour: "2-digit", minute: "2-digit", hour12: true,
      });
    },
    async cargarHistorial() {
      if (!this.proveedor?.id_proveedor) return;
      this.cargando = true;
      try {
        const data = await CuentasPorPagarService.obtenerHistorialPagosProveedor(
          this.proveedor.id_proveedor, this.id_usuario
        );
        this.historial = data || [];
      } catch (e) {
        this.toast.error("Error al cargar historial: " + e.message);
        this.historial = [];
      } finally {
        this.cargando = false;
      }
    },
  },
  watch: {
    isVisible(val) {
      if (val) { this.historial = []; this.cargarHistorial(); }
    },
  },
  mounted() {
    // Dark mode — Teleport saca el modal del .app-wrapper, aplicar .dark directamente
    this.isDark = localStorage.getItem('isDarkMode') === 'true';
    this._onToggleDark = () => {
      this.isDark = localStorage.getItem('isDarkMode') === 'true';
    };
    window.addEventListener('poswave:toggle-dark', this._onToggleDark);

    if (this.isVisible) this.cargarHistorial();
  },

  beforeUnmount() {
    window.removeEventListener('poswave:toggle-dark', this._onToggleDark);
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,100..900;1,100..900&display=swap");
* { font-family: "Montserrat", sans-serif; box-sizing: border-box; }

.modal-overlay {
  position: fixed; inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex; justify-content: center; align-items: flex-start;
  padding: 2vh 1rem; z-index: 9100; overflow-y: auto;
}
.modal-content {
  background: white; padding: 20px; border-radius: 12px;
  width: 700px; max-width: 90vw; max-height: 95vh;
  overflow-y: auto; box-shadow: 0 8px 32px rgba(0,0,0,0.2); margin: auto;
  color: #212529; animation: slideUp 0.2s ease;
}
@keyframes slideUp {
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
}
.header-container {
  display: flex; justify-content: space-between; align-items: center;
  margin-bottom: 20px; padding-bottom: 15px; border-bottom: 1px solid #dee2e6;
}
.modal-title { font-size: 1rem; font-weight: 700; margin: 0; color: #212529; }
.btn-close {
  background: none; border: none; font-size: 1.4rem; cursor: pointer;
  color: #6c757d; padding: 4px; width: 32px; height: 32px;
  display: flex; align-items: center; justify-content: center;
  border-radius: 6px; transition: all 0.2s;
}
.btn-close:hover { background: #e9ecef; color: #212529; }

.info-box {
  background: #f8f9fa; padding: 15px; border-radius: 8px;
  margin-bottom: 20px; display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 15px;
}
.info-item { font-size: 13px; color: #495057; }
.info-item strong { color: #212529; }
.estado-badge { padding: 2px 8px; border-radius: 9999px; font-size: 12px; font-weight: 600; }
.estado-pendiente { background: #fff3cd; color: #856404; }
.estado-pagado   { background: #d1e7dd; color: #0f5132; }
.estado-parcial  { background: #d1ecf1; color: #0c5460; }

.resumen-cuenta {
  background: #fff3cd; padding: 15px; border-radius: 8px; margin-bottom: 20px;
  border: 1px solid #ffc107;
}
.resumen-item { display: flex; justify-content: space-between; margin-bottom: 8px; font-size: 14px; color: #495057; }
.resumen-item:last-child { margin-bottom: 0; font-weight: 700; font-size: 15px; border-top: 1px solid #dee2e6; padding-top: 8px; color: #212529; }
.pagado  { color: #0f5132; font-weight: 700; }
.pendiente { color: #dc3545; font-weight: 700; }

.historial-section h3 { margin: 0 0 15px; color: #212529; font-size: 1rem; font-weight: 700; }
.loading, .no-data { text-align: center; padding: 20px; color: #6c757d; font-style: italic; }

.historial-list { max-height: 400px; overflow-y: auto; display: flex; flex-direction: column; gap: 10px; }
.pago-item { border: 1px solid #dee2e6; border-radius: 8px; overflow: hidden; }
.pago-header {
  background: #f8f9fa; padding: 12px;
  display: flex; justify-content: space-between; align-items: center;
  border-bottom: 1px solid #dee2e6;
}
.pago-fecha { display: flex; align-items: center; gap: 5px; color: #6c757d; font-size: 13px; }
.pago-monto { font-weight: 700; color: #856404; font-size: 15px; }
.pago-details { padding: 12px; background: white; }
.pago-metodo, .pago-factura, .pago-notas, .pago-usuario {
  display: flex; align-items: center; gap: 8px;
  margin-bottom: 5px; font-size: 13px; color: #6c757d;
}
.pago-metodo { color: #856404; font-weight: 600; }
.transfer-number { color: #6c757d; font-weight: normal; }
.pago-usuario { margin-bottom: 0; }

.button-container { display: flex; justify-content: center; padding-top: 15px; border-top: 1px solid #dee2e6; margin-top: 20px; }
.btn { padding: 8px 20px; border: none; border-radius: 8px; cursor: pointer; font-size: 14px; font-family: "Montserrat", sans-serif; font-weight: 600; transition: all 0.2s; }
.btn-cerrar { background: #6c757d; color: white; }
.btn-cerrar:hover { background: #545b62; }

/* ===== DARK MODE ===== */
.dark .modal-content { background: #2d2d2d; color: #ffffff; }
.dark .header-container { border-color: #404040; }
.dark .modal-title { color: #ffffff; }
.dark .btn-close { color: #ffffff; }
.dark .btn-close:hover { background: #404040; color: #ffffff; }
.dark .info-box { background-color: #1e1e1e; }
.dark .info-item { color: #ffffff; }
.dark .info-item strong { color: #ffffff; }
.dark .resumen-cuenta { background-color: #1e2e1e; border-color: #404040; }
.dark .resumen-item { color: #ffffff; }
.dark .resumen-item:last-child { border-color: #404040; }
.dark .historial-section h3 { color: #ffffff; }
.dark .loading, .dark .no-data { color: #aaaaaa; }
.dark .historial-list { border-color: #404040; }
.dark .pago-item { border-color: #404040; }
.dark .pago-header { background-color: #3a3a3a; border-color: #404040; }
.dark .pago-fecha { color: #aaaaaa; }
.dark .pago-details { background-color: #2d2d2d; }
.dark .pago-metodo { color: #4a9eff; }
.dark .pago-factura, .dark .pago-notas, .dark .pago-usuario { color: #cccccc; }
.dark .transfer-number { color: #aaaaaa; }
.dark .button-container { border-color: #404040; }
.dark .btn-cerrar { background: #4a4a4a; color: #ffffff; }
.dark .btn-cerrar:hover { background: #5a5a5a; }

@media screen and (max-width: 768px) {
  .modal-content { width: 95vw; padding: 15px; }
  .info-box { grid-template-columns: 1fr; }
  .pago-header { flex-direction: column; gap: 10px; }
}
</style>
